import { memo } from "react";
import { useTranslation } from "react-i18next";

import { EventTypeEnum } from "shared/types";

import {
  ChartDateFieldEnum,
  CountTypeEnum,
  MAX_LIMIT,
} from "pages/Dashboard/components/constants";
import ClaimsTable from "pages/Dashboard/components/EventTables/ClaimsTable";
import SignalEventsTable from "pages/Dashboard/components/EventTables/SignalEventsTable";
import {
  getYAxisKey,
  processChartDataForStackedBars,
} from "pages/Dashboard/components/utils";
import { MainTableComponentProps } from "pages/Dashboard/types";
import { formatChartDateTick } from "pages/shared/schemaDefinitions/SchemaSmartGroupsTable/utils";

import Card from "features/components/Card";
import SmartGroupBarChart from "features/ui/charts/BarChart/SmartGroupBarChart";
import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { extractAdvancedFilterRelationInfo } from "features/ui/Filters/FilterBuilder/utils";
import { MarkdownContent } from "features/ui/MarkdownContent";
import { SMART_TABLE_HEADER_OFFSET_PX } from "features/ui/MaterialTable/constants";

import { ChartTimeGrouping, SmartGroupData } from "./types";

interface SmartGroupDetailProps {
  row: SmartGroupData;
  eventType: EventTypeEnum;
  countType: CountTypeEnum;
  showOnlyEventDateData: boolean;
  chartLabel?: string;
  eventsPeriod: string;
  getCombinedEventFilters: (
    groupID: string
  ) => FilterGroupState<FilterRowState>;
  vehiclesFilters: FilterGroupState<FilterRowState>;
  onBadRequest: MainTableComponentProps["onBadRequest"];
  config: MainTableComponentProps["config"];
  sharedTableFilterState: MainTableComponentProps["sharedTableFilterState"];
  chartTimeGrouping: ChartTimeGrouping;
  // Only the sticky column-header setting is forwarded to the nested
  // MaterialTable. The nested footer is left at its default (non-sticky)
  // position so it sits at the bottom of the nested table rather than
  // pinning to the viewport under the outer table.
  stickyHeader?: boolean;
}

const SmartGroupDetailPanel = memo(
  ({
    row,
    eventType,
    countType,
    showOnlyEventDateData,
    chartLabel,
    eventsPeriod,
    getCombinedEventFilters,
    vehiclesFilters,
    onBadRequest,
    config,
    sharedTableFilterState,
    chartTimeGrouping,
    stickyHeader,
  }: SmartGroupDetailProps) => {
    const { t } = useTranslation();
    const yAxisKey = getYAxisKey(countType, eventType);

    const { processedData, stackedBarKey } = processChartDataForStackedBars(
      showOnlyEventDateData,
      row.filteredChart,
      row.allTimeChart,
      yAxisKey
    );

    // Get the appropriate table component
    const getTableComponent = () => {
      switch (eventType) {
        case EventTypeEnum.CLAIM:
          return ClaimsTable;
        case EventTypeEnum.SIGNAL_EVENT:
          return SignalEventsTable;
        default:
          return ClaimsTable;
      }
    };

    const groupingPeriod = t(
      `schema:dashboard.granularity_${chartTimeGrouping}`
    );

    const getXAxisLabel = (dateField: string): string => {
      switch (dateField) {
        case ChartDateFieldEnum.EVENT_DATE:
          return t("schema:dashboard.axis_occurrence", {
            period: groupingPeriod,
          });

        case ChartDateFieldEnum.VEHICLE_MANUFACTURED_AT:
          return t("schema:dashboard.axis_production", {
            period: groupingPeriod,
          });

        case ChartDateFieldEnum.ADVANCED_FILTERS_DATE: {
          const lineName = extractAdvancedFilterRelationInfo(
            vehiclesFilters,
            true,
            config.advancedFilterDateParentAttribute,
            config.advancedFilterRelationValueAttribute,
            config.advancedFilterDateChildAttribute
          );
          if (lineName) {
            return t("schema:dashboard.axis_line_exit", {
              lineName,
              period: groupingPeriod,
            });
          }

          console.error("No line name found for advanced filters date field");

          return t("schema:dashboard.axis_production_line_exit", {
            period: groupingPeriod,
          });
        }

        default:
          return t("schema:dashboard.axis_production", {
            period: groupingPeriod,
          });
      }
    };

    const xAxisLabel = getXAxisLabel(config.chartSettings.chartDateField);
    const TableComponent = getTableComponent();

    return (
      <div className="bg-white">
        <div className="px-10 pt-4 pb-2 flex gap-8">
          <div className="flex-shrink-0">
            <b>{t("schema:dashboard.ai_summary")}</b>
            <div className="w-[500px]">
              <MarkdownContent content={row.groupDescription} />
            </div>
          </div>
          {processedData && processedData.length > 0 && (
            <div className="flex-1">
              <Card className="p-4">
                <SmartGroupBarChart
                  data={processedData}
                  xAxisKey="date"
                  yAxisKey={yAxisKey}
                  stackedBarKey={stackedBarKey}
                  chartTitle={chartLabel}
                  yAxisLabel={chartLabel}
                  xAxisLabel={xAxisLabel}
                  eventsPeriod={eventsPeriod}
                  height={200}
                  xAxisProps={{
                    tickFormatter: formatChartDateTick,
                  }}
                />
              </Card>
            </div>
          )}
        </div>
        <TableComponent
          eventFilters={getCombinedEventFilters(row.groupID)}
          pageKey={`smart_group_${eventType.toLowerCase()}_table_${row.groupID}`}
          {...{ vehiclesFilters, onBadRequest, config }}
          rowTestId="smart-group-action-click"
          limit={
            row.filteredCount && row.filteredCount < MAX_LIMIT
              ? row.filteredCount
              : MAX_LIMIT
          }
          smartGroupId={row.groupID}
          hideExport
          showCount={true}
          smartGroups={false}
          onSmartGroupsChange={() => {}}
          sharedTableFilterState={sharedTableFilterState}
          stickyHeader={stickyHeader}
          stickyTopOffset={
            stickyHeader ? SMART_TABLE_HEADER_OFFSET_PX : undefined
          }
        />
      </div>
    );
  }
);

export default SmartGroupDetailPanel;
